import { defaultErrorHandler } from 'volta/utils/error-utils';

import { assert } from '@ember/debug';
import Route from '@ember/routing/route';
import RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';

import type Transition from '@ember/routing/-private/transition';
import type SessionService from 'ember-simple-auth/services/session';
import type Config from 'volta/models/config';
import type User from 'volta/models/user';
import type AppSettings from 'volta/services/app-settings';
import type AuthorizationService from 'volta/services/authorization';
import type LocalizationService from 'volta/services/localization';
import type SessionUserService from 'volta/services/session-user';
export default class ProtectedRoute extends Route {
  // Services
  // ~~~~~
  @service router!: RouterService;
  @service session!: SessionService;
  @service sessionUser!: SessionUserService;
  @service authorization!: AuthorizationService;
  @service appSettings!: AppSettings;
  @service localization!: LocalizationService;

  // Lifecycle Hooks
  // ~~~~~

  async beforeModel(transition: Transition) {
    super.beforeModel(transition);
    this.session.requireAuthentication(transition, 'login');
    try {
      if (transition.to.name === 'protected.index') {
        return this.router.transitionTo('protected.dashboard');
      }
      const user = await this.sessionUser.reloadUser();

      if (user) {
        const [config, tenant] = await Promise.all([
          this.appSettings.fetchConfigsTask.perform(),
          this.appSettings.fetchTenant.perform()
        ]);
        this.authorization.setup(user);
        await this.localization.setTranslation(this.localization.defaultLocale.value, true);
        await this.initUserLocale(user, this.appSettings?.defaultConfig);
        return {
          user,
          config,
          tenant
        };
      }
    } catch (error) {
      defaultErrorHandler(error);
      return await this.session.invalidate();
    }
  }

  // Helpers
  // ~~~~~

  /**
   * Initialise the application locale options from the user preferences
   *
   * @param user The session user
   */
  async initUserLocale(user?: User, config?: Config) {
    assert('Cannot initialize user locale, no user given', user);
    assert('Cannot initialize default locale, no config given', config);
    this.localization.setTimeZone(user?.preferences.timeZone);
    const locale = user?.preferences.locale ?? config?.settings?.locale;
    return await this.localization.setTranslation(locale, true);
  }
}
